import { useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { omit } from 'lodash';
import JSZip from 'jszip';
import { ThemeProvider, useTheme } from '@material-ui/core/styles';
import {
  AppBar,
  Button,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import TransectDataTable from '../visualization/TransectDataTable';
import Export from '../../../services/export';
import { exportCSV, exportJSON, exportSession } from '../../../common/utils';
import { INTERNALS } from '../../../common/metadata';
import { ResultsCacheHolder } from '../../../store/ducks/imagery/saga/shoreline';
import { downloadBaselineAsKML, downloadTransectsAsKML } from '../../../algorithms/utils/turf';
import { exportGeoJson } from '../../../algorithms/shoreline/geoJsonExport';
import { shapeTransectData, buildGeoJsonData, sortOutput } from './transectExportUtils';
import { useRegisterGeoJsonExport, useRegisterPdfExport, useRegisterSessionExport, useThemeMode } from '../../contexts/index.jsx';
import { createShorelineTheme } from '../../pages/Shoreline/theme.js';
import { buildSessionExport } from '../../../algorithms/shoreline/sessionExport';
import { buildShorelineReport, REPORT_MODULE } from '../../../algorithms/shoreline/pdfReport';
import { captureImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { APP_ROOT_SELECTOR } from '../../../common/captureElement';
import { eeEval } from '../../../algorithms/utils';

let intervalCodeShowErrorShorelineImages = null;
function showErrorShorelineImages() {
  const val = window.sessionStorage.getItem('shorelineImagesErrors');
  if (val === null) return;
  const list = JSON.parse(val);
  let component = document.getElementById('shoreline_images_errors');
  if (list.length > 0 && component !== null) {
    component.style.display = 'block';
    component.value = 'Images not processed (' + list.length + '):';
    list.forEach((el) => {
      component.value += '\n' + el.dateFormated + ' - ' + el.message;
    });
    clearInterval(intervalCodeShowErrorShorelineImages);
  }
}

const CoastlineEvolutionDialog = ({ open, close }) => {
  const { shorelineThemeMode } = useThemeMode();
  const outerTheme = useTheme();
  const dialogTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [shorelineThemeMode, outerTheme],
  );

  const transects = useSelector((state) => {
    return state.results.coastlineData
      ? state.results.coastlineData.transectData
      : ResultsCacheHolder.transacts != null
        ? ResultsCacheHolder.transacts
        : [];
  });
  const baselineData = useSelector((state) => {
    return state.results.baselineData ? state.results.baselineData : {};
  });
  const shorelineData = useSelector((state) =>
    state.results.coastlineData
      ? state.results.coastlineData.shorelineData
      : ResultsCacheHolder.shorelines != null
        ? ResultsCacheHolder.shorelines
        : [],
  );

  const metadata = useSelector((state) => state.acquisition.metadata, shallowEqual);
  /**
   * The name of the KML the area came from, when it came from one. The export is named after it, so
   * a folder of saved analyses says which stretch of coast each one is.
   */
  const kmlFileName = useSelector((state) => state.acquisition.kmlFileName);
  const satelliteIndex = useSelector((state) => state.acquisition.satelliteIndex);
  const coordinates = useSelector((state) => state.acquisition.coordinates);
  const chosenImages = useSelector((state) => state.results.chosenImages, shallowEqual);
  const chosenAnalyzedImagesDate = useSelector((state) => state.results.evaluatedImages);
  const urlAnalyzedImages = chosenAnalyzedImagesDate?.flatMap((epochMs) => {
    const isoDate = typeof epochMs === 'string' ? epochMs : new Date(epochMs).toISOString().replace(/\.\d{3}Z$/, '');

    const matches = metadata.filter((m) => m.date === isoDate);

    return matches.map((m) => ({ date: isoDate, url: m.thumbnail }));
  });

  const transectData = shapeTransectData(transects);
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const [tab, setTab] = useState(0);
  const [transectsAnchorEl, setTransectsAnchorEl] = useState(null);
  const [kmlAnchorEl, setKmlAnchorEl] = useState(null);

  const exportSessionState = () => {
    const data = buildSessionExport({
      satelliteIndex,
      coordinates,
      chosenImages,
      baselineCoords: baselineData.coords,
      transectData: transects,
      shorelineData,
    });
    exportSession(data, kmlFileName);
  };
  useRegisterSessionExport('shoreline', coordinates ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes the analysis's geometry - area, baseline, transects, coastlines - to a
   * GeoJSON file. The coastlines are still an Earth Engine list at this point, so they are
   * evaluated here, the same way the session export does it.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const exportGeoJsonLayers = () => {
    const shorelineFeatures = shorelineData?.getInfo ? shorelineData.getInfo() : shorelineData;
    if (!exportGeoJson({ coordinates, baselineCoords: baselineData.coords, transectData, shorelineFeatures })) {
      notifyError(t('forms.acquisition.geoJsonExport.empty'));
    }
  };
  useRegisterGeoJsonExport('shoreline', coordinates ? exportGeoJsonLayers : null);

  /**
   * @function reportText
   * @description Every piece of wording the report needs, translated here and handed to the
   * builder, which stays a pure function of the analysis - see {@link buildShorelineReport}.
   * @memberof module:Core
   * @returns {Object}
   */
  const reportText = () => {
    const key = (name) => `forms.core.pdfReport.${name}`;
    const own = (name) => `forms.core.pdfReport.shoreline.${name}`;
    const group = (name) => t(own(name), { returnObjects: true, defaultValue: t(key(name), { returnObjects: true }) });
    return {
      ...t(key('common'), { returnObjects: true }),
      ...t(own('text'), { returnObjects: true }),
      subtitle: t(key('subtitle'), { date: new Date().toLocaleString() }),
      missingImage: (caption) => t(key('missingImage'), { caption }),
      screenCaption: t(key('screenCaption')),
      rates: group('rates'),
      classes: group('classes'),
      transects: group('transects'),
      transectNote: {
        complete: (values) => t(key('transectComplete'), values),
        truncated: (values) => t(key('transectTruncated'), values),
      },
      transectComplete: (values) => t(key('transectComplete'), values),
      transectTruncated: (values) => t(key('transectTruncated'), values),
      forecastYears: (values) => t(key('forecastYears'), values),
      missingTideNote: (values) => t(own('missingTideNote'), values),
      tide: {
        range: (values) => t(own('tideRangeValue'), values),
        span: (values) => t(own('tideSpanValue'), values),
      },
      errors: group('errors'),
      byDate: group('byDate'),
      byTransect: group('byTransect'),
      bays: group('bays'),
      controlPoints: group('controlPoints'),
      waterlines: group('waterlines'),
      cover: group('cover'),
    };
  };

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The map is captured from the screen rather than fetched, because what this module draws is a
   * Google Maps overlay and not an image with a URL - see {@link captureImageForReport}.
   * @memberof module:Core
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText();
      const screen = await captureImageForReport(APP_ROOT_SELECTOR, text.screenCaption);
      const report = buildShorelineReport({
        coordinates,
        baselineCoords: baselineData.coords,
        transectData,
        shorelineFeatures: shorelineData?.evaluate ? await eeEval(shorelineData) : shorelineData,
        chosenImages,
        kmlFileName,
        images: [screen],
        text,
        className: (value) => t(`forms.core.pdfReport.esteves.${value}`, { defaultValue: value }),
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      notifyError(t('forms.core.pdfReport.failed'));
    }
  };
  useRegisterPdfExport(REPORT_MODULE, coordinates ? exportPdfReport : null);


  if (window.location.href.indexOf('bathymetry') !== -1) return false;
  const tabs = [<TransectDataTable data={transectData} />];
  intervalCodeShowErrorShorelineImages = setInterval(showErrorShorelineImages, 3000);

  const exportZipImages = async () => {
    if (urlAnalyzedImages.length === 0) return alert('No images to export.');

    const zip = new JSZip();
    for (let i = 0; i < urlAnalyzedImages.length; i++) {
      try {
        const { date, url } = urlAnalyzedImages[i];
        const response = await fetch(url);
        if (!response.ok) {
          console.error(`Erro ao baixar imagem ${i + 1}:`, response.status);
          continue;
        }
        const blob = await response.blob();
        const filename = `image_${date.replace(/[:/\\]/g, '-')}.png`;
        zip.file(filename, blob);
      } catch (error) {
        console.error(`Erro ao processar imagem ${i + 1}:`, error);
      }
    }

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = 'coastline_images.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Erro ao criar ZIP:', error);
      alert('Erro ao exportar imagens. Verifique o console.');
    }
  };

  return (
    <ThemeProvider theme={dialogTheme}>
    <Dialog open={open} maxWidth="lg" fullWidth onClose={() => close()}>
      <DialogTitle>{t('forms.shorelineAnalysis.title')}</DialogTitle>
      <DialogContent>
        <Paper>
          <AppBar position="static" color="default">
            <Tabs value={tab} indicatorColor="primary" textColor="primary" onChange={(e, tab) => setTab(tab)}>
              <Tab label={t('forms.shorelineAnalysis.transectsReport.title')} />
            </Tabs>
          </AppBar>
          {tabs[tab]}
          <textarea
            id="shoreline_images_errors"
            rows="5"
            readOnly
            style={{ width: '100%', display: 'none', fontSize: '10' }}></textarea>
        </Paper>
      </DialogContent>
      <DialogActions style={{ display: 'flex', justifyContent: 'space-around' }}>
        <div id={'export_buttons'} style={{ display: 'flex', gap: '10px' }}>
          {tab === 0 && (
            <div>
              <Button onClick={(e) => setTransectsAnchorEl(e.currentTarget)} color="primary">
                {t('forms.shorelineAnalysis.exports.title')}
              </Button>
              <Menu
                anchorEl={transectsAnchorEl}
                open={Boolean(transectsAnchorEl)}
                onClose={() => setTransectsAnchorEl(null)}>
                <MenuItem
                  onClick={() => {
                    let data = sortOutput(transectData);
                    exportCSV(data, 'transects.csv');
                    setTransectsAnchorEl(null);
                  }}>
                  {t('forms.shorelineAnalysis.exports.csv')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    let data = buildGeoJsonData(transectData);
                    exportJSON(data, 'transects.json');
                    setTransectsAnchorEl(null);
                  }}>
                  {t('forms.shorelineAnalysis.exports.json')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    Export.table.toDevice.asShapefileGroup(
                      [
                        {
                          features: [
                            {
                              geometry: {
                                type: 'LineString',
                                coordinates: baselineData.coords || [],
                              },
                              properties: {},
                            },
                          ],
                        },

                        {
                          features: shorelineData.getInfo().map((shoreline) => ({
                            ...shoreline,
                            properties: omit(shoreline.properties, [INTERNALS]),
                          })),
                        },

                        {
                          features: transects.map((transect) => ({
                            ...transect,
                            properties: omit(transect.properties, [INTERNALS]),
                          })),
                        },
                      ],
                      Export.defaultOptions.device.shapefileGroup('layers', 'baseline', 'coastlines', 'transects'),
                    );
                    setTransectsAnchorEl(null);
                  }}>
                  {t('forms.shorelineAnalysis.exports.shp')}
                </MenuItem>
              </Menu>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button onClick={(e) => setKmlAnchorEl(e.currentTarget)}>{t('forms.shorelineAnalysis.exports.kml')}</Button>
            <Menu anchorEl={kmlAnchorEl} open={Boolean(kmlAnchorEl)} onClose={() => setKmlAnchorEl(null)}>
              <MenuItem
                onClick={() => {
                  downloadTransectsAsKML(transects);
                  setKmlAnchorEl(null);
                }}>
                {t('forms.map.transects.title')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  downloadBaselineAsKML(baselineData.coords);
                  setKmlAnchorEl(null);
                }}>
                {t('forms.map.baseline')}
              </MenuItem>
            </Menu>
          </div>
          <Button color="default" onClick={() => exportZipImages()}>
            {t('forms.shorelineAnalysis.exports.images')}
          </Button>
        </div>
        <Button color="secondary" onClick={() => close()}>
          {t('forms.shorelineAnalysis.close')}
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
};

export default CoastlineEvolutionDialog;
